import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { generateSiteContent } from '@utils/lorem-generator';
import { initSmoothScroll } from '@utils/smooth-scroll';
import type { SiteContent } from '@types/content';
import i18next from '../../locales/i18n';

// Import components - Eager load (above fold)
import '../ui/bnb-logo';
import '../layout/bnb-header';
import '../sections/bnb-hero';

// Below fold components will be lazy loaded

@customElement('bnb-app')
export class BnbApp extends LitElement {
  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      width: 100%;
      overflow-x: hidden;
    }

    .app-container {
      min-height: 100vh;
      width: 100%;
      display: flex;
      flex-direction: column;
      background: white;
      overflow-x: hidden;
    }

    main {
      flex: 1;
      width: 100%;
    }

    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      font-size: 1.5rem;
      color: #666;
    }

    /* Reserve space for lazy loaded sections (prevent CLS) */
    bnb-what-we-do {
      min-height: 500px;
    }

    bnb-about {
      min-height: 500px;
    }

    bnb-contact {
      min-height: 400px;
    }

    bnb-partners {
      min-height: 300px;
    }

    bnb-footer {
      min-height: 106px;
    }

    .loading-spinner {
      width: 50px;
      height: 50px;
      border: 3px solid #f3f3f3;
      border-top: 3px solid #2563eb;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin-right: 1rem;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .error {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      color: #dc2626;
      font-size: 1.2rem;
      text-align: center;
      padding: 2rem;
    }

    .sr-only {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border-width: 0;
    }

    .skip-link {
      position: absolute;
      top: -40px;
      left: 0;
      background: #00bcd4;
      color: white;
      padding: 0.75rem 1.5rem;
      text-decoration: none;
      font-weight: 600;
      z-index: 300;
      border-radius: 0 0 4px 0;
    }

    .skip-link:focus {
      top: 0;
    }
  `;

  @state()
  private content: SiteContent | null = null;

  @state()
  private loading = true;

  @state()
  private error: string | null = null;

  @state()
  private belowFoldLoaded = false;

  @state()
  private loadingAnnouncement = '';

  // Cached references for performance
  private headerElement: Element | null = null;
  private heroSection: Element | null = null;
  private ticking = false;
  private boundHandleScroll = this.handleScroll.bind(this);
  private boundHandleLanguageChange = this.handleLanguageChange.bind(this);

  private handleLanguageChange() {
    this.requestUpdate();
  }

  connectedCallback() {
    super.connectedCallback();
    this.loadContent();
    this.initializeApp();

    // Listen for language changes and re-render
    i18next.on('languageChanged', this.boundHandleLanguageChange);
  }

  private async loadContent() {
    try {
      // TODO: Replace with actual content loading
      // For now, generate Lorem Ipsum content
      this.content = generateSiteContent();

      this.loading = false;
    } catch (err) {
      this.error = 'Failed to load content. Please refresh the page.';
      this.loading = false;
      console.error('Error loading content:', err);
    }
  }

  private initializeApp() {
    // Initialize smooth scrolling
    requestAnimationFrame(() => {
      initSmoothScroll({ offset: 80, duration: 800 });

      // Cache DOM references after render
      this.headerElement = this.shadowRoot?.querySelector('bnb-header') || null;
      this.heroSection = document.querySelector('#hero');
    });

    // Add scroll listener with proper cleanup support
    this.handleScroll();
    window.addEventListener('scroll', this.boundHandleScroll, { passive: true });

    // Lazy load below-fold components
    this.loadBelowFoldComponents();
  }

  private async loadBelowFoldComponents() {
    try {
      this.loadingAnnouncement = i18next.t('common.loadingSections');

      // Load below-fold components asynchronously
      await Promise.all([
        import('../sections/bnb-what-we-do'),
        import('../sections/bnb-about'),
        import('../sections/bnb-contact'),
        import('../sections/bnb-partners'),
        import('../layout/bnb-footer')
      ]);

      this.belowFoldLoaded = true;
      this.loadingAnnouncement = i18next.t('common.allLoaded');

      // Clear announcement after a brief delay
      setTimeout(() => {
        this.loadingAnnouncement = '';
      }, 1000);
    } catch (err) {
      console.error('Error loading below-fold components:', err);
      this.loadingAnnouncement = i18next.t('common.loadingError');
    }
  }

  private handleScroll() {
    // Throttle scroll events using requestAnimationFrame
    if (!this.ticking) {
      requestAnimationFrame(() => {
        if (this.headerElement) {
          const scrolled = this.heroSection
            ? window.scrollY > this.heroSection.clientHeight * 0.8
            : window.scrollY > 50;

          if (scrolled) {
            this.headerElement.classList.add('scrolled');
          } else {
            this.headerElement.classList.remove('scrolled');
          }
        }
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  render() {
    if (this.loading) {
      return html`
        <div class="loading">
          <div class="loading-spinner"></div>
          ${i18next.t('common.loadingBNB')}
        </div>
      `;
    }

    if (this.error) {
      return html`
        <div class="error">
          <div>
            <h2>${i18next.t('common.error')}</h2>
            <p>${this.error}</p>
          </div>
        </div>
      `;
    }

    if (!this.content) {
      return html`<div class="error">${i18next.t('common.noContent')}</div>`;
    }

    return html`
      <div class="app-container">
<!--        <a href="#main" class="skip-link">${i18next.t('common.skipToMain')}</a>-->

        <!-- ARIA live region for dynamic announcements -->
        <div
          aria-live="polite"
          aria-atomic="true"
          class="sr-only"
          role="status"
        >
          ${this.loadingAnnouncement}
        </div>

        <!-- ARIA live region for language change announcements -->
        <div
          id="lang-announcement"
          aria-live="polite"
          aria-atomic="true"
          class="sr-only"
          role="status"
        ></div>

        <bnb-header .title=${'BrandNewBroadcast'} role="banner"></bnb-header>

        <main id="main" role="main">
          <bnb-hero id="hero"></bnb-hero>

          ${this.belowFoldLoaded ? html`
            <bnb-what-we-do id="what-we-do"></bnb-what-we-do>

            <bnb-about id="about"></bnb-about>

            <bnb-contact id="contact"></bnb-contact>

            <bnb-partners id="partners"></bnb-partners>
          ` : html`
            <div style="min-height: 100vh; display: flex; align-items: center; justify-content: center;">
              <div class="loading-spinner"></div>
            </div>
          `}
        </main>

        ${this.belowFoldLoaded ? html`<bnb-footer id="footer" role="contentinfo"></bnb-footer>` : ''}
      </div>
    `;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.boundHandleScroll);
    i18next.off('languageChanged', this.boundHandleLanguageChange);
  }
}
